import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    Paper,
    Divider,
    Select,
    MenuItem,
    IconButton,
    useTheme
} from '@mui/material';
import {
    Close as CloseIcon,
    Bedtime,
    WbSunny,
    WbTwilight,
    DoNotDisturb,
    Flare,
    Today,
    ArrowForwardIos,
    ArrowBackIos
} from '@mui/icons-material';

interface IncompleteDefaultAvailabilityModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (availabilities: Record<string, string>) => Promise<void>;
    defaultAvailabilities: Record<string, any>[];
}

const IncompleteDefaultAvailabilityModal: React.FC<IncompleteDefaultAvailabilityModalProps> = ({
    open,
    onClose,
    onSubmit,
    defaultAvailabilities
}) => {
    const theme = useTheme();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availabilityStep, setAvailabilityStep] = useState(0);
    const [weekDayAvailability, setWeekDayAvailability] = useState<Record<string, string>>({
        Sunday: 'Unavailable',
        Monday: 'Unavailable',
        Tuesday: 'Unavailable',
        Wednesday: 'Unavailable',
        Thursday: 'Unavailable',
        Friday: 'Unavailable',
        Saturday: 'Unavailable',
    });

    // Load existing availabilities on mount
    React.useEffect(() => {
        if (open && defaultAvailabilities.length > 0) {
            const loaded: Record<string, string> = { ...weekDayAvailability };
            defaultAvailabilities.forEach((av: Record<string, any>) => {
                const day = av.dayUserId?.split('#')[1];
                if (day) {
                    const dayName = Object.keys(weekDayAvailability).find(
                        d => d.slice(0, 3) === day
                    );
                    if (dayName) {
                        loaded[dayName] = av.availability && JSON.stringify(av.availability) !== "{}" ? 'Set' : 'Unavailable';
                    }
                }
            });
            setWeekDayAvailability(loaded);
        }
    }, [open, defaultAvailabilities]);

    const days = Object.keys(weekDayAvailability);
    const completedCount = Object.values(weekDayAvailability).filter(v => v !== 'Unavailable').length;
    const isComplete = Object.values(weekDayAvailability).some(v => v !== 'Unavailable');

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmit(weekDayAvailability);
            onClose();
        } catch (error) {
            console.error('Error submitting availability:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Set Your Default Availability
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Complete {completedCount} of {days.length} days
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Set Up Your Availability
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Let your manager know when you're generally available to work. You can update this anytime in your dashboard and adjust your availability
                        before each schedule is published.
                    </Typography>
                    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                        {Object.keys(weekDayAvailability).map((weekDay, index) => (
                            <Box display={index === availabilityStep ? 'block' : 'none'} key={index} sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                    {weekDay}s
                                </Typography>
                                <Divider sx={{ mb: 2 }} variant="middle" />

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <IconButton
                                        aria-label="previous day"
                                        onClick={() => setAvailabilityStep((s) => Math.max(0, s - 1))}
                                        disabled={index === 0}
                                    >
                                        <ArrowBackIos />
                                    </IconButton>

                                    <Box sx={{ flex: 1 }}>
                                        {/* Controlled Select for availability */}
                                        <Select
                                            value={weekDayAvailability[weekDay] ?? 'Unavailable'}
                                            onChange={(e) => {
                                                const val = e.target.value as string;
                                                setWeekDayAvailability((prev) => ({
                                                    ...prev,
                                                    [weekDay]: val,
                                                }));
                                            }}
                                            renderValue={(value) => (
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1, color: value === "Unavailable" ? `${theme.palette.error.light}`
                                                        : value === "Morning (5am - 12pm)" ? `${theme.palette.info.light}`
                                                            : value === "Afternoon (12pm - 5pm)" ? `${theme.palette.warning.light}`
                                                                : value === "Evening (5pm - 10pm)" ? `${theme.palette.primary.light}`
                                                                    : value === "Night (10pm - 5am)" ? `${theme.palette.secondary.light}`
                                                                        : `${theme.palette.primary.main}`
                                                }}>
                                                    {value === 'Unavailable' ? <DoNotDisturb /> : value === 'Morning (5am - 12pm)' ? <Flare /> : value === 'Afternoon (12pm - 5pm)' ? <WbSunny /> : value === 'Evening (5pm - 10pm)' ? <WbTwilight /> : value === 'Night (10pm - 5am)' ? <Bedtime /> : <Today />}
                                                    <Typography variant="body2">{value}</Typography>
                                                </Box>
                                            )}
                                            fullWidth
                                        >
                                            {['Unavailable', 'Morning (5am - 12pm)', 'Afternoon (12pm - 5pm)', 'Evening (5pm - 10pm)', 'Night (10pm - 5am)', 'All Day (9am - 5pm)'].map((option) => (
                                                <MenuItem key={option} value={option} sx={{
                                                    color: option === "Unavailable" ? `${theme.palette.error.light}`
                                                        : option === "Morning (5am - 12pm)" ? `${theme.palette.info.light}`
                                                            : option === "Afternoon (12pm - 5pm)" ? `${theme.palette.warning.light}`
                                                                : option === "Evening (5pm - 10pm)" ? `${theme.palette.primary.light}`
                                                                    : option === "Night (10pm - 5am)" ? `${theme.palette.secondary.light}`
                                                                        : `${theme.palette.primary.main}`
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {option === 'Unavailable' ? <DoNotDisturb /> : option === 'Morning (5am - 12pm)' ? <Flare /> : option === 'Afternoon (12pm - 5pm)' ? <WbSunny /> : option === 'Evening (5pm - 10pm)' ? <WbTwilight /> : option === 'Night (10pm - 5am)' ? <Bedtime /> : <Today />}
                                                        <Typography variant="body2">{option}</Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>

                                    <IconButton
                                        aria-label="next day"
                                        onClick={() => setAvailabilityStep((s) => Math.min(6, s + 1))}
                                        disabled={index === 6}
                                    >
                                        <ArrowForwardIos />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Paper>

                    {/* Availability legend: 7 colored boxes reflecting each day's selection */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, mb: 3 }}>
                        {Object.keys(weekDayAvailability).map((d, i) => {
                            const val = weekDayAvailability[d];
                            const isSet = val !== 'Unavailable';

                            return (
                                <Box
                                    key={d}
                                    onClick={() => setAvailabilityStep(i)}
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: 0.5,
                                        backgroundColor: isSet ? theme.palette.success.main : theme.palette.grey[300],
                                        border: availabilityStep === i ? `2px solid ${theme.palette.primary.dark}` : `1px solid ${theme.palette.divider}`,
                                        cursor: 'pointer',
                                        opacity: isSet ? 1 : 0.5,
                                    }}
                                    title={`${d}: ${isSet ? 'Set' : 'Not set'}`}
                                />
                            )
                        })}
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isComplete}
                        sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 600, boxShadow: 3 }}
                    >
                        {isSubmitting ? 'Saving...' : 'Complete Setup'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default IncompleteDefaultAvailabilityModal;